import { useEffect, useState } from "react";
import { ScrollText, ChevronLeft, ChevronRight } from "lucide-react";
import { AdminLayout } from "./AdminLayout";
import { AuditRow } from "./AdminDashboard";
import { api, apiErr } from "../../lib/api";
import { Loader, Empty, GhostButton } from "../../components/UI";
import { toast } from "sonner";

const PAGE_SIZE = 20;

export default function AdminAuditLog() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/admin/audit-log", { params: { page, page_size: PAGE_SIZE } });
        if (!active) return;
        setEntries(data.entries || []);
        setTotal(data.total || 0);
      } catch (e) {
        if (active) toast.error(apiErr(e, "Failed to load audit log"));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <AdminLayout>
      <div data-testid="admin-audit-log-page">
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#E5B842] font-mono font-bold mb-2">Control Center</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-slate-50 leading-none flex items-center gap-3">
            <ScrollText className="w-8 h-8 text-[#E5B842] shrink-0" /> Audit Log
          </h1>
          <p className="text-slate-400 mt-2 text-sm sm:text-base">{total} recorded admin action{total === 1 ? "" : "s"}.</p>
        </div>

        {loading ? (
          <Loader label="Loading audit log…" />
        ) : (
          <>
            {entries.length === 0 ? (
              <Empty label="No admin actions recorded yet" />
            ) : (
              <div className="rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.20)] divide-y divide-[rgba(212,175,55,0.10)]" data-testid="admin-audit-list">
                {entries.map((a) => <AuditRow key={a.id} a={a} />)}
              </div>
            )}
            <div className="flex items-center justify-between mt-5">
              <div className="text-xs font-mono uppercase tracking-wider text-slate-500">Page {page} / {totalPages}</div>
              <div className="flex gap-2">
                <GhostButton data-testid="admin-audit-prev" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft className="w-4 h-4" /> Prev
                </GhostButton>
                <GhostButton data-testid="admin-audit-next" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                  Next <ChevronRight className="w-4 h-4" />
                </GhostButton>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
